<template>
	<div ref="wizardContainer" class="max-w-3xl py-10 mx-auto space-y-8">
		<!-- progress bar -->
		<div class="w-full h-2 overflow-hidden bg-gray-200 rounded-full">
			<div class="h-2 transition-all duration-300 bg-blue-600" :style="{ width: progress + '%' }" />
		</div>

		<QuestionSection v-if="typedSectionId" ref="questionSection" :section-id="typedSectionId" />

		<!-- Error message -->
		<div
			v-if="store.errorMessage"
			class="p-4 mb-6 border border-yellow-200 rounded-lg bg-yellow-50"
		>
			<div class="flex items-start gap-3">
				<svg
					class="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0"
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 20 20"
					fill="currentColor"
				>
					<path
						fill-rule="evenodd"
						d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
						clip-rule="evenodd"
					/>
				</svg>
				<div>
					<h3 class="text-sm font-medium text-yellow-800">Hinweis</h3>
					<p class="mt-1 text-sm text-yellow-700">
						{{ store.errorMessage }}
					</p>
				</div>
			</div>
		</div>

		<!-- navigation -->
		<div class="flex justify-between pt-6">
			<button
				class="inline-flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 rounded-md shadow disabled:opacity-50"
				:disabled="step === 1"
				@click="goBack"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					class="w-5 h-5"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
				>
					<path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
				</svg>
				<span>Zurück</span>
			</button>

			<button
				class="inline-flex items-center gap-2 px-6 py-2 font-medium text-white bg-blue-600 rounded-md shadow disabled:opacity-50"
				:disabled="!sectionComplete || store.isLoading"
				@click="goNext"
			>
				<!-- Loading spinner -->
				<svg
					v-if="store.isLoading"
					class="w-5 h-5 animate-spin"
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
				>
					<circle
						class="opacity-25"
						cx="12"
						cy="12"
						r="10"
						stroke="currentColor"
						stroke-width="4"
					/>
					<path
						class="opacity-75"
						fill="currentColor"
						d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
					/>
				</svg>

				<span>{{
					store.isLoading
						? 'Wird verarbeitet...'
						: isLast && currentPhase === 2
							? 'Ergebnisse'
							: isLast && currentPhase === 1
								? 'Weiter zu Teil 2'
								: 'Weiter'
				}}</span>

				<svg
					v-if="!isLast && !store.isLoading"
					xmlns="http://www.w3.org/2000/svg"
					class="w-5 h-5"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
				>
					<path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
				</svg>
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, watch, onMounted, ref, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useWizardStore } from '../stores/wizard';
import { useConversionTracking } from '@/composables/usePageTracking';
import { useBreakPageExperiment } from '@/composables/useBreakPageExperiment';
import type { SectionId } from '../data/learningTest';

import QuestionSection from './QuestionSection.vue';

// Template refs
const wizardContainer = ref<HTMLElement>();
const questionSection = ref<InstanceType<typeof QuestionSection>>();

// Props from router
interface Props {
	phase?: number;
	step?: string;
}

const props = withDefaults(defineProps<Props>(), {
	phase: 1,
	step: '1',
});

const store = useWizardStore();
const route = useRoute();
const router = useRouter();
const { trackFunnelStep } = useConversionTracking();
const { isBreakEnabled, initExperiment } = useBreakPageExperiment();

// Initialize phase and step from props/route
onMounted(async () => {
	// Initialise experiment assignment early so VARIANT_ASSIGNED fires before navigation.
	// This ensures the tracking event is sent even if the user bounces before phase 1 ends.
	initExperiment();

	const phaseNum = props.phase || 1;
	const stepNum = Number(props.step || route.params.step || 1);

	store.setPhase(phaseNum as 1 | 2);
	store.setStep(stepNum);

	// Track phase start
	// Note: Test session is already started by coordination layer in prepareTestStart()
	if (phaseNum === 1 && stepNum === 1) {
		trackFunnelStep('wizard_phase1_started');
	} else if (phaseNum === 2 && stepNum === 1) {
		trackFunnelStep('wizard_phase2_started');
	}
});

// Watch for route changes and handle smooth scrolling
watch(
	() => [route.params.step, props.phase],
	async ([stepParam, phase], [oldStepParam]) => {
		const stepNum = Number(stepParam || 1);
		const phaseNum = phase || 1;
		const oldStepNum = Number(oldStepParam || 1);

		store.setPhase(phaseNum as 1 | 2);
		if (stepNum >= 1 && stepNum <= store.totalStepsInCurrentPhase) {
			store.setStep(stepNum);

			// Smooth scroll to top and focus when step changes (but not on initial load)
			if (oldStepParam && stepNum !== oldStepNum) {
				await nextTick();
				scrollToTop();
				// Also focus the question section title for accessibility and mobile UX
				setTimeout(() => {
					if (questionSection.value && typeof questionSection.value.focusTitle === 'function') {
						questionSection.value.focusTitle();
					}
				}, 300); // Small delay to ensure smooth scroll completes
			}
		}
	},
);

const step = computed(() => store.step);
const currentPhase = computed(() => store.currentPhase);
const currentSectionId = computed(() => store.currentSectionId);
const totalSteps = computed(() => store.totalStepsInCurrentPhase);

// Properly typed section ID for template - avoids type assertion
const typedSectionId = computed((): SectionId | null => {
	const sectionId = currentSectionId.value;
	if (!sectionId || sectionId === 'basic-info') return null;
	return sectionId as SectionId;
});

const sectionComplete = computed(() => {
	if (!currentSectionId.value) return false;
	return store.isSectionComplete(currentSectionId.value);
});

const isLast = computed(() => step.value === totalSteps.value);
const progress = computed(() => (step.value / totalSteps.value) * 100);

// Smooth scroll to top function
function scrollToTop() {
	// Try multiple scroll targets for best UX
	const targets = [
		wizardContainer.value,
		document.querySelector('.max-w-3xl'),
		document.documentElement,
		document.body,
	];

	for (const target of targets) {
		if (target) {
			target.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
				inline: 'nearest',
			});
			break;
		}
	}

	// Fallback: scroll window to top
	window.scrollTo({
		top: 0,
		behavior: 'smooth',
	});
}

async function goBack() {
	if (step.value > 1) {
		const prev = step.value - 1;
		const phaseRoute = currentPhase.value === 1 ? 'wizard-phase1' : 'wizard-phase2';
		await router.push({ name: phaseRoute, params: { step: prev.toString() } });
		// Scroll will be handled by the route watcher
	}
}

async function goNext() {
	if (!sectionComplete.value) return;

	if (isLast.value) {
		if (currentPhase.value === 1) {
			// Complete Phase 1
			store.completePhase1();
			trackFunnelStep('wizard_phase1_completed', {
				questionsAnswered: Object.keys(store.answers).length,
			});

			if (isBreakEnabled.value) {
				// Break variant: show the motivational break page
				trackFunnelStep('break_page_shown', {
					experimentId: 'break_page_v1',
					variantId: 'break',
				});
				await router.push({ name: 'learning-break' });
			} else {
				// Control variant: skip break page → start phase 2 directly
				trackFunnelStep('break_page_skipped', { variantId: 'control' });
				await router.push({ name: 'wizard-phase2', params: { step: '1' } });
			}
			// Scroll to top for next page
			await nextTick();
			scrollToTop();
		} else {
			// Complete Phase 2 and generate basic result (client-side)
			store.completePhase2();
			trackFunnelStep('wizard_phase2_completed', {
				questionsAnswered: Object.keys(store.answers).length,
			});
			await store.generateBasicResult();
			await router.push(`/results/${store.testId}`);
			// Scroll to top for results
			await nextTick();
			scrollToTop();
		}
	} else {
		const next = step.value + 1;
		const phaseRoute = currentPhase.value === 1 ? 'wizard-phase1' : 'wizard-phase2';
		await router.push({ name: phaseRoute, params: { step: next.toString() } });
		// Scroll will be handled by the route watcher
	}
}
</script>
